package fr.frux.shared.data.local

import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.HarvestPart
import fr.frux.shared.domain.model.PlantForm
import fr.frux.shared.domain.model.SpeciesRef
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.TreeStatus
import kotlin.time.Instant
import fr.frux.core.database.Tree as DbTree

/**
 * Mappe une ligne du cache SQLDelight vers le domaine (#58). Retourne `null` si une enum
 * obligatoire (status, certainty, forme) porte une valeur inconnue — la ligne vient d'une version
 * plus récente du schéma et se filtre plutôt que de planter. Une `harvest_part` inconnue retombe
 * sur `null`, comme un référentiel sans fenêtre déclarée.
 *
 * Les champs volatils ou per-user (photos détaillées, réactions, isFavorite, reportedByMe,
 * distance) ne sont pas persistés et gardent leur défaut : le cache sert la projection
 * carte/liste, le détail complet reste réseau.
 */
fun DbTree.toDomain(): Tree? {
    val status = enumOrNull<TreeStatus>(status)
    val certainty = enumOrNull<Certainty>(certainty)
    val form = enumOrNull<PlantForm>(species_form)
    if (status == null || certainty == null || form == null) return null
    return Tree(
        id = id,
        species =
            SpeciesRef(
                id = species_id,
                commonNameFr = species_common_name_fr,
                scientificName = species_scientific_name,
                form = form,
            ),
        latitude = latitude,
        longitude = longitude,
        status = status,
        description = description,
        photoUrl = photo_url,
        createdBy = created_by,
        createdAt = created_at,
        updatedAt = updated_at,
        lastObservedAt = last_observed_at?.let { Instant.fromEpochMilliseconds(it) },
        harvestPart = harvest_part?.let { enumOrNull<HarvestPart>(it) },
        certainty = certainty,
        disputed = disputed != 0L,
        // Pas de colonne en cache (#208) : hors ligne, le bandeau retombe sur son libellé
        // générique — dégradation assumée plutôt qu'une migration pour un détail d'affichage.
        disputedKinds = emptyList(),
        siteId = site_id,
    )
}

/** Mappe un [Tree] domaine vers une ligne du cache, horodatée par l'appelant ([cachedAt]). */
fun Tree.toCacheRow(cachedAt: Long): DbTree =
    DbTree(
        id = id,
        species_id = species.id,
        species_common_name_fr = species.commonNameFr,
        species_scientific_name = species.scientificName,
        species_form = species.form.name,
        harvest_part = harvestPart?.name,
        latitude = latitude,
        longitude = longitude,
        status = status.name,
        certainty = certainty.name,
        disputed = if (disputed) 1L else 0L,
        description = description,
        photo_url = photoUrl,
        created_by = createdBy,
        created_at = createdAt,
        updated_at = updatedAt,
        last_observed_at = lastObservedAt?.toEpochMilliseconds(),
        cached_at = cachedAt,
        site_id = siteId,
    )

internal inline fun <reified E : Enum<E>> enumOrNull(name: String): E? = enumValues<E>().firstOrNull { it.name == name }
